import React from 'react'
import { observable, toJS } from 'mobx'
import { observer } from 'mobx-react'
import { Row, Col, CardGroup, Button } from 'react-bootstrap'
import { IoMdFunnel as RefineIcon } from 'react-icons/io'
import { MicroFrontendLoader } from 'src/views/components/MicroFrontends/MicroFrontendLoader'
import { pageNames } from 'src/routes/pathParams'
import { unSubscribeEvent, subscribe, publish } from 'src/pubsub'
import { PUBSUB } from 'src/pubsub/pubEventNames'
import { Loader } from 'src/views/components/Loader'
import { CatalogContainer, catalogContainer } from 'src/models'
import { catalogState } from 'src/models/Catalog/catalogState'
import {
  ProductCard,
  SideFilter,
  ProductSort,
  Pagination,
  toastState,
  CustomPrice,
} from 'src/views/components'
import { productDeps, catalogDeps } from 'src/deps'
import {
  application,
  i18nTranslate,
  convertToBoolean,
  getDefaultSku,
  isProductStatusDisabled,
  isPOAdminScreen,
} from 'src/utils'
import { isMerchPLP, APPConfig } from 'config/appConfig'
import './styles.scss'
import { GRID_COLS, BUNDLE_PRODUCT, KIT_PRODUCT, SKU_PRODUCT } from 'src/utils/constant'
const DEFAULT_PAGE_SIZE = 12

@observer
class MySiteAdminCatalog extends React.Component {
  showCatalogModalToken = null
  maxProductCapToken = null
  @observable showCatalogView = false
  @observable isFilterOpen = false
  @observable catalogData = null
  @observable catalogResponse = null
  @observable isLoading = false
  @observable error = null
  @observable refineButtonRef = React.createRef()
  @observable selectedFilters = observable.array([])
  // Plain arrays: ProductCard owns checkbox state, so selection must not re-render the grid
  selectedProducts = []
  selectedProductsDataArray = []
  @observable originalProducts = []
  @observable selectedSort = []
  @observable sortOptions = []
  nameSpace = 'ssr-resource'

  @observable totalPage = 1
  @observable activePage = 1
  @observable isResetting = false

  constructor(props) {
    super(props)
    this.MAX_SELECTED_PRODUCTS = 12 
    this.currentProductCount = 0 
    this.currentSort = { sortField: 'featured', sortDirection: 'ASC' }
    this.showCatalogView = true
    this.onSelectedProductsChange = props.onSelectedProductsChange
    const {
      isMysiteRedesignFeatureEnabled = false,
      enableProductOfferV2 = false,
    } = APPConfig?.getAppConfig() || {}
    this.isMysiteRedesign =
      convertToBoolean(isMysiteRedesignFeatureEnabled) ||
      convertToBoolean(enableProductOfferV2)
  }

  updateSortOptions = sort => {
    if (sort && sort.length > 0) {
      const selectedKey =
        this.currentSort?.sortField && this.currentSort?.sortDirection
          ? `${this.currentSort.sortField} ${this.currentSort.sortDirection}`.trim()
          : 'featured'

      this.sortOptions = sort.map(option => ({
        ...option,
        selected:
          option.key === selectedKey ||
          option.key === this.currentSort?.sortField,
      }))
    }
  }

  componentDidMount() {
    this.subscribeShowCatalogEvent()
    this.subscribeMaxProductCapEvent()
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.catalogData !== this.props.catalogData &&
      this.props.catalogData
    ) {
      this.catalogData = this.props.catalogData
      this.showCatalogView = true
      const sort = this.props.catalogData?.products?.sort || []
      this.updateSortOptions(sort)
    }
    if (
      prevProps.onSelectedProductsChange !== this.props.onSelectedProductsChange
    ) {
      this.onSelectedProductsChange = this.props.onSelectedProductsChange
    }
    if (prevProps.filterProps !== this.props?.filterProps) {
      let filters = []
      try {
        filters = this.props?.filterProps
          ? JSON.parse(this.props?.filterProps)?.filters || []
          : []
      } catch (error) {
        console.warn('Failed to parse filterProps JSON:', error)
        filters = []
      }
      this.selectedFilters.replace(filters)
    }
  }

  subscribeShowCatalogEvent = () => {
    this.showCatalogModalToken = subscribe(
      PUBSUB.SHOW_CATALOG_PAGE_MYSITE_REDESIGN,
      this.renderCatalogView
    )
  }

  subscribeMaxProductCapEvent = () => {
    this.maxProductCapToken = subscribe(
      PUBSUB.MAX_SELECTED_PRODUCTS_CAP,
      this.handleMaxProductCapUpdate
    )
  }

  handleMaxProductCapUpdate = (msg, data) => {
    const { maxSelectedProducts,currentProductCount } = data || {}
    const maxCapNumber = Number(maxSelectedProducts)
    const currentCount = Number(currentProductCount) || 0
    
    if (typeof maxCapNumber === 'number' && maxCapNumber > 0) {
      this.MAX_SELECTED_PRODUCTS = maxCapNumber
      this.currentProductCount = currentCount
    }
  }


  componentWillUnmount() {
    unSubscribeEvent(this.showCatalogModalToken)
    unSubscribeEvent(this.maxProductCapToken)
    this.showCatalogView = false
  }

  publishSelectedProductsArray = () => {
    const selectedProductsData = {
      action: 'PRODUCTS_SELECTED',
      selectedProducts: this.selectedProducts,
      selectedProductsDataArray: [...this.selectedProductsDataArray],
      totalSelected: this.selectedProducts.length,
      filters: [...this.selectedFilters],
    }
    publishSelectedProducts(selectedProductsData)
  }

  getProductType = parsedProduct => {
    return parsedProduct?.type?.toLowerCase?.() || ''
  }

  hasSKUs = parsedProduct => {
    return (
      parsedProduct?.sku &&
      Array.isArray(parsedProduct?.sku) &&
      parsedProduct?.sku?.length > 0
    )
  }

  getProductIdentifierForSelection = parsedProduct => {
    if (!parsedProduct) return ''
    const productType = this.getProductType?.(parsedProduct)

    if (productType === BUNDLE_PRODUCT || productType === KIT_PRODUCT) {
      return parsedProduct?.identifier
    }

    if (this.hasSKUs(parsedProduct)) {
      const defaultSku = getDefaultSku(parsedProduct)
      if (defaultSku?.identifier) {
        return defaultSku?.identifier
      }
    }
    return parsedProduct?.identifier
  }

  buildProductMetadata = (parsedProduct, productId, productType, defaultSkuId = null) => {
    const productWithSelectionMetadata = {
      ...parsedProduct,
      selectionIdentifier: productId,
      defaultSkuId: defaultSkuId || productId, // Track card's default SKU for variant matching
      selectionStrategy: productType === BUNDLE_PRODUCT ? BUNDLE_PRODUCT : SKU_PRODUCT,
    }
    
    if (isPOAdminScreen() && parsedProduct?.selectedBundleVariants) {
      productWithSelectionMetadata.selectedBundleVariants = parsedProduct.selectedBundleVariants
    }
    
    return productWithSelectionMetadata
  }

  updateExistingSelection = (existingIndex, productId, productMetadata) => {
    const oldSkuId = this.selectedProductsDataArray[existingIndex]?.selectionIdentifier
    const oldSkuIndex = this.selectedProducts.indexOf(oldSkuId)
    if (oldSkuIndex !== -1) {
      this.selectedProducts[oldSkuIndex] = productId
    }
    this.selectedProductsDataArray[existingIndex] = productMetadata
  }

  addNewSelection = (productId, productMetadata) => {
    if (!this.selectedProducts?.includes?.(productId)) {
      this.selectedProducts.push(productId)
      this.selectedProductsDataArray.push(productMetadata)
    }
  }

  handleDeselection = (productId, parsedProduct = null, selectionContext = {}) => {
    const { defaultSkuId } = selectionContext

    if (defaultSkuId) {
      const productToRemove = this.selectedProductsDataArray.find(
        product =>
          product?.defaultSkuId === defaultSkuId &&
          (!parsedProduct?.identifier || product?.identifier === parsedProduct.identifier)
      )

      if (productToRemove) {
        const productIndex = this.selectedProductsDataArray.indexOf(productToRemove)
        if (productIndex !== -1) {
          this.selectedProducts.splice(productIndex, 1)
          this.selectedProductsDataArray.splice(productIndex, 1)
        }
        return
      }
    }

    // For multi-variant products in POAdmin: find and remove by product identifier
    if (parsedProduct?.identifier) {
      const productToRemove = this.selectedProductsDataArray.find(
        p => p?.identifier === parsedProduct.identifier
      )
      if (productToRemove) {
        const skuIdToRemove = productToRemove.selectionIdentifier
        this.selectedProducts = this.selectedProducts.filter(id => id !== skuIdToRemove)
        this.selectedProductsDataArray = this.selectedProductsDataArray.filter(
          product => product?.identifier !== parsedProduct.identifier
        )
        return
      }
    }
    
    // Default behavior: remove by productId
    this.selectedProducts = this.selectedProducts.filter(id => id !== productId)
    this.selectedProductsDataArray = this.selectedProductsDataArray.filter(
      product => product?.selectionIdentifier !== productId
    )
  }

  checkMaxProductsReached = (isAlreadySelected) => {
    const availableProductsLength = this.catalogData?.storedSelectedProductsLength
    const totalSelected = (this.selectedProducts?.length || 0) + (availableProductsLength || 0)
    
    if ((totalSelected + this.currentProductCount) >= this.MAX_SELECTED_PRODUCTS && !isAlreadySelected) {
      toastState.setToastMessage(
        i18nTranslate(
          'mysitepo.maxProductSelectionThresholdReached',
          'Maximum {count} products can be selected. Please remove a product to add a new one.'
        ).replace('{count}', this.MAX_SELECTED_PRODUCTS),
        { nameSpace: this.nameSpace },
        false
      )
      return true
    }
    return false
  }


  notifyParentAndPublish = () => {
    if (this.props.onSelectedProductsChange) {
      this.props.onSelectedProductsChange([...this.selectedProductsDataArray])
    }
    this.publishSelectedProductsArray()
  }

  handleProductSelection = (
    productId = '',
    isSelected = false,
    parsedProduct = null,
    selectionContext = {} // { isVariantChange, defaultSkuId }
  ) => {
    const { isVariantChange = false, defaultSkuId } = selectionContext
    const productType = this.getProductType?.(parsedProduct)
    
    let existingIndex
    if (isVariantChange && defaultSkuId && parsedProduct?.identifier) {
      // Variant change: find by product identifier + defaultSkuId combination
      existingIndex = this.selectedProductsDataArray?.findIndex(
        product => 
          product?.identifier === parsedProduct.identifier && 
          product?.defaultSkuId === defaultSkuId
      )
    } else {
      // Normal selection: find by selectionIdentifier
      existingIndex = this.selectedProductsDataArray?.findIndex(
        product => product?.selectionIdentifier === productId
      )
    }
    
    const isAlreadySelected = existingIndex !== -1 || this.selectedProducts?.includes?.(productId)
    
    // Early returns for invalid states
    if (!isSelected && !isAlreadySelected) return
    if (isSelected && this.selectedProducts?.includes?.(productId) && existingIndex === -1) return
    
    const wasAtSelectionLimit =
      (this.selectedProducts?.length || 0) + this.currentProductCount >=
      this.MAX_SELECTED_PRODUCTS

    if (isSelected) {
      if (this.checkMaxProductsReached(isAlreadySelected)) return
      
      const productMetadata = this.buildProductMetadata(parsedProduct, productId, productType, defaultSkuId)
      
      if (isAlreadySelected) {
        this.updateExistingSelection(existingIndex, productId, productMetadata)
      } else {
        this.addNewSelection(productId, productMetadata)
      }
    } else {
      this.handleDeselection(productId, parsedProduct, selectionContext)
    }
    
    const isAtSelectionLimit =
      (this.selectedProducts?.length || 0) + this.currentProductCount >=
      this.MAX_SELECTED_PRODUCTS

    // Other cards' disabled state only needs recomputing when the limit is crossed either way
    if (wasAtSelectionLimit !== isAtSelectionLimit) {
      this.forceUpdate()
    }
    this.notifyParentAndPublish()
  }

  renderCatalogView = (msg, data) => {
    const { catalogData } = data || {}
    if (catalogData) {
      this.showCatalogView = true
      this.catalogData = catalogData
      this.originalProducts = [...(catalogData?.products?.product || [])]
      this.sortedProducts = [...(catalogData?.products?.product || [])]
      const sort = catalogData?.products?.sort || []
      this.updateSortOptions(sort)

      this.updatePaginationData(catalogData?.products?.pageableInfo)
      const categoryId = catalogData?.selectedCategory
      
      // Store PO Admin locale and storeId for use in product API calls
      if (catalogData?.locale) {
        catalogContainer.poAdminLocale = catalogData.locale
      }
      if (catalogData?.storeId) {
        catalogContainer.poAdminStoreId = catalogData.storeId
      }
    }
  }

  updatePaginationData = pageableInfo => {
    if (!pageableInfo) return

    const {
      page = 1,
      size = DEFAULT_PAGE_SIZE,
      count = 0,
      totalCount = 0,
    } = pageableInfo || {}
    this.totalPage = Math.ceil(totalCount / size)
    this.activePage = page
  }

  getSelectedProductData = productIdentifier => {
    // Find product in selectedProductsDataArray by checking selectionIdentifier or product identifier
    return this.selectedProductsDataArray.find(p => {
      if (p?.selectionIdentifier === productIdentifier) return true
      // Also check product identifier for bundles/kits
      return p?.identifier === productIdentifier
    })
  }

  renderProductCard = props => {
    const {
      listItem: product,
      index,
      handleSizeCalc = '20',
      cardHeight = '20',
    } = props
    if (!product) {
      console.error('Product is undefined for renderProductCard')
      return null
    }
    const parsedProduct = this.parseProductData?.(product)
    const productIdentifierForSelection =
      this.getProductIdentifierForSelection(parsedProduct)
    const categoryId = this.catalogData?.selectedCategory || ''
    const productBadge = parsedProduct?.properties?.productBadge || ''
    const productPromotion = parsedProduct?.promotion || []
    const selectedSKU =
      catalogDeps?.getDefaultSelectedSku?.(parsedProduct) || []
    const skuId =
      (selectedSKU && selectedSKU?.[0] && selectedSKU?.[0]?.identifier) || ''
    const defaultSku =
      parsedProduct?.sku?.find(sku => sku?.default === true) ||
      parsedProduct?.sku?.[0]
    const productDetails =
      selectedSKU?.[0] && (catalogDeps?.isLoadFromSKU || isMerchPLP)
        ? selectedSKU?.[0]
        : {
            ...product,
            ...(selectedSKU?.[0] && {
              prices: selectedSKU?.[0]?.prices || [],
              priceFacets: selectedSKU?.[0]?.priceFacets || [],
              totalValue: selectedSKU?.[0]?.totalValue || 0,
            }),
          }
    const productType = parsedProduct?.type || ''
    const productId = parsedProduct?.identifier || ''
    const isProductComparable = catalogState?.isProductComparable(
      this.catalogData,
      parsedProduct
    )
    const productIdentifier = parsedProduct?.identifier

    // SKU ids are not unique across products, so a card is identified by product +
    // its default SKU. Matching defaultSkuId keeps the card selected after a variant change.
    const isSelected =
      this.selectedProductsDataArray?.some(
        selectedProduct =>
          selectedProduct?.identifier === productIdentifier &&
          selectedProduct?.defaultSkuId === productIdentifierForSelection
      ) || false

    const isStatusDisabled = isProductStatusDisabled(parsedProduct)
    const isStatusBasedDisabled = isStatusDisabled
    const availableProductsLength = this.catalogData?.storedSelectedProductsLength || 0
    const isCheckboxDisabled =
      ((this.selectedProducts?.length || 0) + availableProductsLength + this.currentProductCount) >= this.MAX_SELECTED_PRODUCTS && !isSelected
    const pvValues = parsedProduct?.sku?.map(sku => sku?.defaultpriceFacets?.PV)

    // Get selected product data to restore variant selection state (POAdmin only)
    const selectedProductData = this.getSelectedProductData(productIdentifier)
    const selectedVariantSkuId = selectedProductData?.selectedVariantSkuId
    const selectedVariantLabel = selectedProductData?.selectedVariantLabel
    const selectedBundleVariants = selectedProductData?.selectedBundleVariants
    const selectedKitSubItems = selectedProductData?.selectedKitSubItems
    const previouslySelectedBundleTotalValue = selectedProductData?.totalValue

    return (
      <ProductCard
        key={`product-card_${productId || index}_${productIdentifierForSelection || index}`}
        product={productDetails}
        productId={productIdentifierForSelection}
        selectionIdentifier={productIdentifierForSelection}
        defaultSkuId={productIdentifierForSelection}
        categoryId={categoryId}
        location={this.props?.location}
        history={this.props?.history}
        currency={parsedProduct?.currency || '$'}
        productType={productType}
        hideAddToCart={true}
        isSelected={isSelected}
        onSelectionChange={this.handleProductSelection}
        isCheckboxDisabled={isCheckboxDisabled}
        isStatusBasedDisabled={isStatusBasedDisabled}
        showSelectedCount={true}
        selectedCount={this.selectedProducts?.length}
        isMySiteRedesign={this.isMysiteRedesign}
        isMySiteAdminPage={!isPOAdminScreen()}
        svPrice={parsedProduct?.defaultpriceFacets?.PV}
        adminPriceType={this.catalogData?.priceType ?? 'retail'}
        maxSelectedProducts={this.MAX_SELECTED_PRODUCTS}
        selectedVariantSkuId={selectedVariantSkuId}
        selectedVariantLabel={selectedVariantLabel}
        selectedBundleVariants={selectedBundleVariants}
        selectedKitSubItems={selectedKitSubItems}
        previouslySelectedBundleTotalValue={previouslySelectedBundleTotalValue}
      />
    )
  }

  handlePagination = async (pageNumber = 1) => {
    this.activePage = pageNumber
    const paginationParams = {
      page: pageNumber,
      size: this.catalogData?.products?.pageableInfo?.size || DEFAULT_PAGE_SIZE,
      ...this.getFilterAndSortParams(),
    }
    this.publishFilterUpdatedArray(pageNumber)
    if (typeof window === 'object') {
      window.scrollTo(0, 0)
    }
  }

  getFilterAndSortParams = () => {
    const params = {}
    if (this.selectedFilters && this.selectedFilters?.length > 0) {
      params.filters = this.selectedFilters
    }
    if (this.selectedSort && this.selectedSort?.length > 0) {
      params.sort = this.selectedSort
    }

    return params
  }

  getFilter = filterValue => {
    if (
      !filterValue ||
      (Array.isArray(filterValue) && filterValue.length === 0)
    ) {
      this.selectedFilters?.replace?.([])
      this.activePage = 1
      this.publishFilterUpdatedArray(1)
      return
    }
    if (
      Array.isArray(filterValue) &&
      filterValue.length > 0 &&
      filterValue?.[0]?.field &&
      filterValue?.[0]?.operation
    ) {
      this.selectedFilters?.replace?.([...filterValue])
      this.activePage = 1
      this.publishFilterUpdatedArray(1)
      return
    }

    const filtersToProcess = Array.isArray(filterValue)
      ? filterValue
      : [filterValue]
    const newFilters = [...this.selectedFilters]

    filtersToProcess.forEach(filter => {
      if (!filter || !filter?.field || !filter?.value) return
      const {
        field,
        value,
        operation = this.getOperationForField(field),
        isSelected = false,
      } = filter
      const existingFilterIndex = newFilters?.findIndex?.(
        filterObj => filterObj.field === field
      )
      if (existingFilterIndex !== -1) {
        const existingFilter = newFilters[existingFilterIndex]
        const existingValues = existingFilter?.value?.split?.(',') || []

        if (!isSelected) {
          const updatedValues =
            existingValues?.filter?.(
              existingValue => existingValue !== value
            ) || []

          if (updatedValues.length > 0) {
            newFilters[existingFilterIndex] = {
              ...existingFilter,
              value: updatedValues?.join?.(','),
            }
          } else {
            newFilters?.splice?.(existingFilterIndex, 1)
          }
        } else if (isSelected) {
          const updatedValues = [...(existingValues || []), value]
          newFilters[existingFilterIndex] = {
            ...existingFilter,
            value: updatedValues.join(','),
          }
        }
      } else if (isSelected) {
        newFilters.push({
          field,
          operation,
          value,
        })
      }
    })
    this.selectedFilters?.replace?.(newFilters)
    this.activePage = 1
    this.publishFilterUpdatedArray(1)
  }
  handleFilterReset = () => {
    this.selectedFilters = []
    this.selectedSort = []
    this.currentSort = { sortField: 'featured', sortDirection: 'ASC' }
    this.activePage = 1
    this.publishFilterUpdatedArray(1)
    toastState.setToastMessage(
      'All filters cleared',
      { nameSpace: this.nameSpace },
      true
    )
  }

  publishFilterUpdatedArray = (pageNumber = null) => {
    const filterData = {
      action: 'FILTERS_UPDATED',
      filters: [...this.selectedFilters],
      totalFilters: this.selectedFilters.length,
    }
    if (this.selectedSort) {
      if (this.selectedSort?.length > 0) {
        filterData.sort = [...this.selectedSort]
      } else {
        filterData.sort = []
      }
    }
    const currentPage = pageNumber || this.activePage
    const pageSize =
      this.catalogData?.products?.pageableInfo?.size || DEFAULT_PAGE_SIZE
    filterData.pagination = {
      page: currentPage,
      size: pageSize,
      totalPages: this.totalPage,
      totalCount: this.catalogData?.products?.pageableInfo?.totalCount || 0,
    }
    if (pageNumber !== null) {
      filterData.action = 'PAGINATION_UPDATED'
      filterData.pageNumber = pageNumber
    }

    publish(PUBSUB.FILTERS_SORT_PAGE_UPDATED_MYSITE_REDESIGN, {
      filterData: filterData,
    })
  }

  getOperationForField = field => {
    if (field.startsWith('range_')) {
      return 'BETWEEN'
    }
    return 'IN'
  }

  getSort = sortValue => {
    this.sortOptions = this.sortOptions?.map(option => ({
      ...option,
      selected: false,
    }))

    if (!sortValue) {
      this.currentSort = { sortField: 'featured', sortDirection: 'ASC' }
      this.selectedSort = []
      this.sortOptions = this.sortOptions?.map(option => ({
        ...option,
        selected: option.key === 'featured',
      }))
      this.activePage = 1
      this.publishFilterUpdatedArray(1)
      return
    }

    if (
      sortValue &&
      typeof sortValue === 'object' &&
      sortValue?.sortField &&
      (sortValue?.sortDirection === '' ||
        sortValue?.sortDirection === null ||
        sortValue?.sortDirection === undefined)
    ) {
      this.currentSort = { sortField: 'featured', sortDirection: '' }
      this.selectedSort = []
      this.sortOptions = this.sortOptions?.map(option => ({
        ...option,
        selected: option.key === 'featured',
      }))
      this.activePage = 1
      this.publishFilterUpdatedArray(1)
      return
    }

    if (
      sortValue &&
      typeof sortValue === 'object' &&
      sortValue?.sortField &&
      sortValue?.sortDirection
    ) {
      this.currentSort = {
        sortField: sortValue?.sortField,
        sortDirection: sortValue?.sortDirection,
      }
      this.selectedSort = [
        {
          field: sortValue?.sortField,
          direction: sortValue?.sortDirection,
        },
      ]

      const selectedKey =
        `${sortValue.sortField} ${sortValue.sortDirection}`.trim()
      this.sortOptions = this.sortOptions?.map(option => ({
        ...option,
        selected:
          option.key === selectedKey || option.key === sortValue.sortField,
      }))

      this.activePage = 1
      this.publishFilterUpdatedArray(1)
    }
  }

  getProductPrice = product => {
    if (product?.sku && product?.sku?.length > 0) {
      const defaultSku =
        product?.sku?.find(sku => sku?.default) || product?.sku?.[0]
      if (defaultSku?.prices && defaultSku?.prices?.length > 0) {
        const price = defaultSku?.prices?.[0]?.amount || 0
        return price
      }
    }

    if (product?.prices && product?.prices?.length > 0) {
      const price = product?.prices?.[0]?.amount || 0
      return price
    }

    if (product?.properties?.price) {
      const price = parseFloat(product?.properties?.price) || 0
      return price
    }
    return 0
  }

  toggleFilter = () => {
    this.isFilterOpen = !this.isFilterOpen
    this.forceUpdate()
  }

  calculatePagination = pageableInfo => {
    if (!pageableInfo) {
      return {
        currentPage: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        totalItems: 0,
        itemsOnCurrentPage: 0,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: false,
      }
    }

    const {
      page = 1,
      size = DEFAULT_PAGE_SIZE,
      count = 0,
      totalCount = 0,
    } = pageableInfo || {}
    const totalPages = Math.ceil(totalCount / size)

    return {
      currentPage: page,
      pageSize: size,
      totalItems: totalCount,
      itemsOnCurrentPage: count,
      totalPages: totalPages,
      hasNextPage: page < totalPages,
      hasPreviousPage: page > 1,
      startIndex: (page - 1) * size + 1,
      endIndex: Math.min(page * size, totalCount),
    }
  }

  productCount = count => {
    if (count > 0) {
      return (
        <span className="product-count-redesign" data-testid="qa-plp-count">
          ({count}{' '}
          {i18nTranslate('plp.matchesCount', 'Products', {
            nameSpace: this.nameSpace,
          })}
          )
        </span>
      )
    }
    return <></>
  }

  parseProductData = product => {
    if (!product) return product

    const parsedProduct = { ...product }
    const stringifiedFields = [
      'benefits',
      'usage',
      'productDetails',
      'seoInformation',
      'results',
      'refund',
      'sustainability',
      'shipping',
    ]

    if (parsedProduct.properties) {
      stringifiedFields.forEach(field => {
        if (
          parsedProduct?.properties?.[field] &&
          typeof parsedProduct?.properties?.[field] === 'string'
        ) {
          try {
            parsedProduct.properties[field] = JSON.parse(
              parsedProduct?.properties?.[field]
            )
          } catch (error) {
            console.warn(`Failed to parse ${field}:`, error)
          }
        }
      })
    }

    if (parsedProduct?.sku && Array.isArray(parsedProduct?.sku)) {
      parsedProduct.sku = parsedProduct?.sku?.map(sku => {
        const parsedSku = { ...sku }
        if (parsedSku?.properties) {
          stringifiedFields.forEach(field => {
            if (
              parsedSku?.properties?.[field] &&
              typeof parsedSku?.properties?.[field] === 'string'
            ) {
              try {
                parsedSku.properties[field] = JSON.parse(
                  parsedSku?.properties?.[field]
                )
              } catch (error) {
                console.warn(`Failed to parse SKU ${field}:`, error)
              }
            }
          })
        }
        return parsedSku
      })
    }

    return parsedProduct
  }

  renderProductsSection = () => {
    if (!this.catalogData || !this.catalogData?.products) {
      return null
    }

    const { facetFilter = [] } = this.catalogData?.products || {}
    const { sort = [] } = this.catalogData?.products || {}
    const products = this.catalogData?.products || []

    const allProducts = this.catalogData?.products?.product || []
    const itemTotalCount =
      this.catalogData?.products?.pageableInfo?.totalCount || 0
    const currencySymbol =
      this.catalogData?.products?.product?.[0]?.sku?.[0]?.prices?.[0]
        ?.currencySymbol ||
      this.catalogData?.products?.product?.[0]?.prices?.[0]?.currencySymbol ||
      '$'

    return (
      <Row noGutters className="catalog-page-wrap mx-auto my-0">
        {/* Side Filter */}
        <Col
          lg={GRID_COLS.SMALL}
          md={GRID_COLS.SMALL}
          className={`side-filter-wrapper ${
            !this.isFilterOpen && !application.isTabletOrLarger
              ? 'd-none'
              : 'd-block'
          } ${isPOAdminScreen() ? 'plp-redesign plp-page po-admin-filter' : ''}`}>
          <SideFilter
            facetFilter={facetFilter}
            facetPosition={this.catalogData?.facetPosition || ''}
            filterProps={JSON.stringify({ filters: this.selectedFilters })}
            handleFilter={this.getFilter}
            handleFilterReset={this.handleFilterReset}
            isResetting={this.isResetting}
            toggleFilter={this.toggleFilter}
            currencySymbol={currencySymbol}
            isFilterOpen={this.isFilterOpen}
          />
        </Col>

        {/* Products Section */}
        <Col
          lg={GRID_COLS.LARGE}
          md={GRID_COLS.LARGE}
          sm={GRID_COLS.FULL}
          xs={GRID_COLS.FULL}
          className={`plp-product-wrapper px-0 pl-md-4 ${
            isPOAdminScreen() ? 'plp-redesign' : ''
          }`}>
          {/* Header with count and sort */}
          <Row
            className="plp-header align-items-center justify-content-between py-2 ml-0 my-1"
            noGutters>
            <Button
              className="refine-button p-0 border-0 bg-transparent d-md-none"
              onClick={this.toggleFilter}
              variant="outline-dark"
              aria-label="refine"
              ref={this.refineButtonRef}>
              <RefineIcon className="refine-icon" />
            </Button>

            <div className="category-count-wrapper d-flex align-items-center">
              <div>
                {this.catalogData?.products?.searchTerm ? (
                  <>{this.productCount?.(itemTotalCount)}</>
                ) : (
                  <>
                    <span className="category-count-wrapper-span">
                      {this.catalogData?.milestoneName}
                    </span>
                    {this.productCount?.(itemTotalCount)}
                  </>
                )}
              </div>
            </div>
            <ProductSort
              sortOptions={this.sortOptions}
              handleSort={this.getSort}
              isMysiteRedesign={this.isMysiteRedesign && !isPOAdminScreen()}
            />
          </Row>

          {/* Product Grid */}
          <CardGroup
            className={`catalog-page ml-0 ssr-catalog-page cardgroup-redesign ${isPOAdminScreen() ? 'plp-redesign plp-page' : ''}`}>
            {products?.product?.map((product, index) =>
              this.renderProductCard({
                listItem: product,
                index,
                handleSizeCalc: '20',
                cardHeight: '20',
              })
            )}
          </CardGroup>
          {/* Pagination */}
          <Pagination
            className="justify-content-center mt-4"
            handlePagination={this.handlePagination}
            totalPages={this.totalPage}
            active={this.activePage}
            alignPagination="center"
          />
        </Col>
      </Row>
    )
  }

  renderSearchTitle = properties => {
    return (
      <Row
        noGutters
        className={`plp-title align-items-center ${properties.className || ''}`}>
        {properties.searchTerm && (
          <h1 className="category-name mr-2 mb-0 pl-3" data-testid="qa-plp-title">
            <span
              className="search-title search-title-small"
              data-testid="qa-plp-count">
              {properties.count}
            </span>
            &nbsp;
            <span className="search-title-small">
              {i18nTranslate('plp.resultsFor', 'results for', {
                nameSpace: this.nameSpace,
              })}
            </span>
            &nbsp;
            <span
              className="search-title search-title-small"
              data-testid="qa-plp-title">
              {properties.searchName?.toUpperCase() || ''}
            </span>
          </h1>
        )}
        {!properties.searchTerm && (
          <h1 className="category-name mr-2 mb-0 pl-3" data-testid="qa-plp-title">
            {properties.title}
          </h1>
        )}
      </Row>
    )
  }

  render() {
    const productSearchDetails = {
      searchTerm: this.catalogData?.products?.searchTerm || '',
      count: this.catalogData?.products?.pageableInfo?.totalCount || 0,
      searchName: this.catalogData?.products?.searchTerm || '',
      title: this.catalogData?.categoryTitle || '',
      className: '',
    }

    return (
      <div>
        {this.showCatalogView && (
          <div
            className="mysite-admin-catalog-page"
            style={{ width: '100%', height: 'auto' }}>
            <div className="plp-title-container">
              {this.renderSearchTitle(productSearchDetails)}
            </div>
            {this.catalogData && (
              <div>
                {this.catalogData.products &&
                Array.isArray(this.catalogData.products.product) &&
                this.catalogData.products.product.length > 0 ? (
                  <>
                    {this.totalPage > 1 && (
                      <p className="pagination-info">
                        Page {this.activePage} of {this.totalPage}
                      </p>
                    )}
                    {this.renderProductsSection()}
                  </>
                ) : (
                  <div className="no-products-found">
                    <h4>
                      {i18nTranslate(
                        'mysite.noProductsFoundForSearch',
                        'We could not find anything for the search word'
                      )}
                    </h4>
                    <p>
                      {i18nTranslate(
                        'mysite.tryDifferentSearch',
                        'Try searching for a different term or check the spelling?'
                      )}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    )
  }
}
export function publishSelectedProducts(selectedProductsData) {
  publish(PUBSUB.SELECTED_PRODUCTS_ARRAY_MYSITE_REDESIGN, {
    selectedProductsData: selectedProductsData,
  })
}
export function publishFilterUpdate(filterData) {
  publish(PUBSUB.FILTERS_SORT_PAGE_UPDATED_MYSITE_REDESIGN, {
    filterData: filterData,
  })
}

export { MySiteAdminCatalog }
export default MySiteAdminCatalog
